// Design tokens for the Due Diligence report (MIKE-322 design).
//
// Shared by user-interface (the client's report) and admin-interface (the
// staff editor that renders the very same markup). They live here rather than
// on a styled-components theme because the two apps declare incompatible
// `DefaultTheme` shapes — see the note at the top of report/Styles.ts.
//
// Anything a component needs in more than one file belongs here; a tint used
// by a single component stays with that component, where the design handoff
// put it.

// ── Typography ───────────────────────────────────────────────────
/** Body copy. */
export const FONT_REPORT = '"Host Grotesk", "Inter", sans-serif';
// The design's display font for numbers/values/titles (bold headline text) —
// distinct from FONT_REPORT (body copy). Loaded in index.html alongside Host
// Grotesk; MIKE-322 components had been writing `font-family:'Figtree'`
// (or, worse, silently falling back to FONT_REPORT) without it ever being
// loaded, so it always rendered as Host Grotesk/Inter until now.
/** Numbers, values and headline titles. */
export const FONT_FIGTREE = '"Figtree", "Host Grotesk", sans-serif';

// ── Ink ──────────────────────────────────────────────────────────
export const TEXT_BODY = "rgba(0, 27, 46, 0.8)";
export const TEXT_HEADING = "rgb(0, 27, 46)";
export const TEXT_META = "rgb(71, 107, 133)";
export const CARD_BORDER = "rgb(222, 225, 232)";

// ── Brand accent (Virtual Mike report identity) ──────────────────
export const BRAND_GREEN = "#A9FDC9";
export const BRAND_NAVY = "#001D2F";

// Paper. Three spellings of one color — #ffffff ×25, #fff ×10 and the CSS
// keyword — across eight files, including the value Theme.ts hands the admin
// editor. (Two keyword uses were left as `white`: in a one-line rule the
// word reads better than the token.)
//
// This one is not about drift: white cannot drift. It is here so the report
// has a word for its ground, the way it has one for its rules and its
// washes, and so that a paper that is not pure white is one line rather than
// thirty-nine.
export const WHITE = "#ffffff";
export const BLACK = "#000000";

// ── The last few that cross a file ───────────────────────────────
// Everything below is here for one reason only: it is written in more than
// one file, which is the rule at the top of this file. Nothing was merged —
// each is a single value that simply never had a name.
//
// What deliberately stays behind: #111827 (5 uses) and #6b7280 (3), both
// only in report/Styles.ts, and #141414 (2), only in
// EnergyCertificateScale.tsx. A color that never leaves its file is not a
// shared token, however often it is written.

/** The app chrome's secondary ink — Theme.colors.font.tertiary. */
export const CHROME_INK = "#565869";
/** The app chrome's muted grey — Theme.colors.font.grey2. */
export const CHROME_GREY = "#989898";
/** Placeholder ink: the editor's "add item" label, an empty-state caption. */
export const TEXT_PLACEHOLDER = "#9ca3af";
/** Ink dark enough to read on TINT_AMBER, where SEMAPHORE.amber.text is not. */
export const AMBER_INK = "#8a6414";
/** The green a linked term is underlined in, so the rule sits under the word
 *  without competing with it. */
export const UNDERLINE_GREEN = "#b7d8c7";

// The report's working green: the ink on an icon badge, a block-number pill,
// a mitigant label, an "Acción" caption — the small affirming marks, always
// printed on a pale ground. 11 declarations across seven files, never named.
//
// It is a third green, not a misspelling of the semaphore's two: it sits
// between SEMAPHORE.green.fill (#35a46e, the solid dot) and .text (#237a50,
// the verdict word), ΔE 10 and 7 from them. The semaphore renders a verdict
// the backend wrote; this renders the report's own voice.
export const GREEN_ACCENT = "#2c8c5e";

// ── Green tint ───────────────────────────────────────────────────
// The pale green ground the report lays under anything affirmative: a
// favorable callout, a "cumplido" pill, an active table row, a highlighted
// column, the conclusion box, a panel's icon badge. Five values across 26
// declarations, ordered lightest to deepest.
//
// Numbered rather than named because that is what it is — a ramp, not five
// roles. Naming them would invent distinctions the design never made, and
// asking whether five steps are needed is easier against a ladder than
// against five adjectives.
//
// Nothing is folded here. Adjacent steps sit ΔE 1.0-2.2 apart and the ramp
// spans 5.3 end to end — and unlike a hairline these are large flat areas,
// where a small difference is *more* visible, not less: the 1px-rule
// argument that merged BORDER_SUBTLE runs backwards on a wash. Dropping a
// step is one deletion here.
//
// Two greens stay out: STATUS_COLORS.green.bg (#e6f9f0), which answers to
// the payload, and #f3fcf8, used once.
export const TINT_GREEN = {
  50: "#f4f9f6",
  100: "#f1f8f4",
  200: "#eaf5ef",
  300: "#e7f3ec",
  400: "#e4f3eb",
} as const;

// TINT_GREEN's two siblings. They get no ramp because they have no ramp:
// amber is one value, red is two — a warm one under a "crítico" callout and
// a pinker one under a "bloqueante" pill. Folding those two would be the
// same mistake as folding the green ladder; they are areas.
// (#fbede6, the criticality card's own, was folded into TINT_RED: ΔE 0.7.)
export const TINT_AMBER = "#fbf3e6";
export const TINT_RED = "#fbeee8";
export const TINT_RED_PINK = "#fbe9ec";

// The fourth tint: the ground of a neutral pill, an unhighlighted matrix
// header tag, an "orientativo" criticality. The hairline commit left this
// one as a literal on the grounds that it is a fill and not a rule, which
// was right — but it is a fill written in two files, so it belongs here as a
// tint rather than out there as a coincidence.
export const TINT_NEUTRAL = "#f0f3f1";

// The edge that goes around a tinted fill — deeper than the fill it borders,
// one per tone. Distinct from BORDER_GREEN, which is the pale rule around an
// *untinted* affirming card, and from BORDER_SUBTLE, which is grey.
export const BORDER_TINT = {
  green: "#d4eadd",
  amber: "#f0e2c6",
  red: "#f2d9ce",
} as const;

// ── Value ink ────────────────────────────────────────────────────
// The report's most-repeated color: the near-black the design prints figures
// and untoned values in. It reads as a semaphore "neutral" in several tone
// maps, but it is not a verdict grey — it is simply the absence of one.
export const TEXT_VALUE = "#14323b";

// One step lighter and warmer than TEXT_VALUE: the dark the executive
// summary prints its totals, its panel headings and a lead paragraph in.
// 11 declarations across five files.
//
// It very nearly folded into TEXT_VALUE — the two sit side by side in
// ExecutiveSummaryPanels.tsx answering the same question (`$total ? this :
// INK.soft` on one line, `$highlight ? TEXT_VALUE : INK.muted` on another),
// which is what drift looks like. But measured they are ΔE 4.0 apart, well
// above the 2.4 line the ink ladder folds on, so it keeps its own name and
// the report keeps rendering as it does. Whether the design wants two darks
// this close is Laura's call; it is now one question in one place.
export const TEXT_LEAD = "#22343b";

// ── Semaphore ────────────────────────────────────────────────────
// The report's verdict vocabulary AND its palette, declared together on
// purpose: these three names only exist in order to pick a color, and keeping
// them apart is what let the same green drift into four hex values.
//
// The names are data, not a local choice — the backend analyzers write them
// into the report JSON (`badge.variant`, `essence.semaphore`, a risk's
// `severity`), so they are spelled the way the payload spells them and cannot
// be renamed without migrating reports.
//
// `fill` paints the solid marks (dots, icon badges, verdict circles); `text`
// paints the words. The design pairs each tone with its own background tint
// per component, and those tints differ on purpose — a pill, a callout box
// and a hero-fact card are tinted differently even at the same verdict — so
// they stay with their component rather than living here.
//
// Two palettes deliberately stay out: STATUS_COLORS (the Tailwind-ish badge
// family in report/Styles.ts, whose fourth state the payload calls `grey`,
// not `neutral`) and FINDING_COLORS (the five finding categories). Both are
// separate visual families in the design, not this three-color semaphore with
// different values.
export const TONE = {
  green: "green",
  amber: "amber",
  red: "red",
  neutral: "neutral",
} as const;

export const SEMAPHORE_TONES = [TONE.green, TONE.amber, TONE.red] as const;
export type SemaphoreTone = (typeof SEMAPHORE_TONES)[number];

// Neutral is the grey the design gives an area that is out of the report's
// scope at this tier ("Fiscal / Urbanístico / Mercado · No procede") — a
// fourth state only some of the semaphore's consumers can render.
export const SEMAPHORE_TONES_WITH_NEUTRAL = [...SEMAPHORE_TONES, TONE.neutral] as const;
export type SemaphoreToneWithNeutral = (typeof SEMAPHORE_TONES_WITH_NEUTRAL)[number];

interface SemaphoreInk {
  fill: string;
  text: string;
}

// Annotated rather than `as const` so a tone added to the vocabulary above
// fails to compile until it is given a color, and vice versa.
export const SEMAPHORE: Record<SemaphoreTone, SemaphoreInk> = {
  green: { fill: "#35a46e", text: "#237a50" },
  amber: { fill: "#c9820e", text: "#c9820e" },
  red: { fill: "#c0392b", text: "#c0392b" },
};

export const SEMAPHORE_WITH_NEUTRAL: Record<SemaphoreToneWithNeutral, SemaphoreInk> = {
  ...SEMAPHORE,
  neutral: { fill: "#9aa7ac", text: "#7c8b91" },
};

// ── Hairlines ────────────────────────────────────────────────────
// The 1px rule the design draws between rows, around cards and under
// headers. It had drifted into seven values (#f0f3f1, #eef1ef, #ecefed,
// #e9eeeb, #eaeeec, #ecf0ee, #e4eae7) across 53 declarations — all within
// ΔE 2.1 of the one below, which on a 1px line is not a difference anyone can
// see. This is the one §2 settled on.
//
// One neighbour keeps its literal because it is not a rule: #e7ecea, the
// road band in ReportChart (ΔE 0.9). A fill is read as an area, and an area
// at ΔE 1 is a choice, not drift. The design's own #f0f3f1 fill was the
// other; it is now TINT_NEUTRAL, for the same reason and in the right
// family.
export const BORDER_SUBTLE = "#e9eeeb";

// ── Surfaces ─────────────────────────────────────────────────────
// The resting ground of a card, row or panel: the off-white the design lays
// under a KPI card, a hero-fact tile, a note box, an areas box, the neutral
// section callout, and the top stop of the gradients that head a panel.
//
// Four literals had been spelled for that one role (#fafbfa ×13, #fcfdfc ×2,
// #fbfcfb, #fbfdfc) — all within ΔE 0.7, which is under the threshold at
// which anyone can tell two near-whites apart even side by side. This is the
// one the design uses almost everywhere.
//
// It is the *untinted* surface. The green-tinted grounds (#f4f9f6, #eaf5ef,
// #f1f8f4) and the amber/red callout tints are verdicts, not this, and keep
// their literals with the component that paints them.
export const SURFACE_CARD = "#fafbfa";

// The neutral grey surface of the report's *chrome* — meta pills, section
// icon circles, the inactive nav pill, image placeholders, row hovers. Two
// design systems had each brought their own: the app's own theme grey
// (#F2F3F7, 7 declarations, and still the value Theme.ts hands the admin
// editor) and Tailwind's gray-100 (#f3f4f6, 9 declarations). ΔE 1.1 apart —
// the same grey, twice. The app's own wins; flipping that is one line here.
//
// STATUS_COLORS.grey.bg in report/Styles.ts is the same Tailwind value but
// stays put: it is the badge palette's fourth state, which answers to the
// payload, not to the chrome.
export const SURFACE_GREY = "#F2F3F7";

// The pale green-grey wash: the ground under a block-number pill, a chart
// card, an image placeholder, a table header band. Nine values across 13
// declarations had drifted through this one idea.
//
// This was three named rungs for one commit, on the claim that adjacent
// steps sat ΔE 1.5 apart and deserved naming rather than folding. The claim
// was wrong: measured, the whole ladder spans ΔE 1.8 end to end and every
// member is within 1.04 of this value — tighter than the ink ladder's fold
// line (2.4) and the chrome rule's (2.8). One value, then.
//
// Two neighbours still stay with their component, per the note at the top of
// this file: #f5f9f6, the bottom stop of the synth strip's gradient, and
// #eef3f0, the plot band of a single chart.
export const WASH = "#f4f7f5";

// One shade under SURFACE_GREY: the ground of a filled data pill and the
// hover state of a chrome link. Not a border — it is read as a fill, which
// is why it keeps a value of its own rather than joining BORDER_GREY below.
export const SURFACE_GREY_DEEP = "#e5e7eb";

// ── Chrome rules ─────────────────────────────────────────────────
// The 1px line the chrome draws under the subheader, around the inactive nav
// pill and around locked/empty cards. Same two-design-systems split as
// SURFACE_GREY: the app theme's #ECECEC (3 declarations, and the value
// Theme.ts hands the admin editor) against Tailwind's gray-200 #e5e7eb (3
// more). ΔE 2.8, which on a 1px rule is the same argument BORDER_SUBTLE
// already settled — and the app's own value wins here too.
//
// BORDER_SUBTLE is the report *body's* hairline, a green-tinted grey; this
// is the app chrome's, untinted. They are separate on purpose.
export const BORDER_GREY = "#ECECEC";

// The dashed rule of a placeholder: the locked-section card and the editor's
// "add item" button. Deliberately darker than BORDER_GREY — a dash has to
// survive the gaps between its segments.
export const BORDER_DASHED = "#d1d5db";

// The green-tinted rule the design draws around anything the report is
// affirming: the "favorable" section callout, the conclusion box, the KPI
// hero, the essence chips, the next-steps buttons. Three values across 13
// declarations (#deeee5 ×7, #e4ede8 ×4, #e1ede7 ×2), ΔE 3.3 apart at the widest —
// close enough to be the same rule, far enough from BORDER_SUBTLE to be a
// different one: this line is meant to read as green, that one as grey.
export const BORDER_GREEN = "#deeee5";

// ── Grey ink ─────────────────────────────────────────────────────
// The ladder of greys the report writes secondary text in, from the darkest
// (table headers, emphasised cells) down to the faintest (icons, list
// bullets). Nine literals across 85 declarations; nine is more rungs than a
// ladder this short needs, but eight of them are separated by ΔE 3-13 — a gap
// you *can* see — so they are named here rather than collapsed. Four more
// values were folded in, all at ΔE 2.4 or less: `quiet` absorbed #8a979c
// (ΔE 2.3, nine declarations), `ghost` absorbed #b0bcb6 (ΔE 2.4, seven),
// `soft` absorbed #55666c (ΔE 2.4, three) and `faint` absorbed #a6b2b7
// (ΔE 1.2, four).
//
// So the line is drawn at ΔE 2.4: below it, one value; above it, a rung.
// That is a wider fold than a wash gets, and deliberately — this is small
// text, closer to a hairline than to an area, and no two of these rungs are
// ever printed next to each other.
//
// Whether the design really means eight steps is a question for Laura, not
// something to settle by eye in a refactor. Now that they are named, the
// answer is a one-line edit here instead of a sweep across eight files.
//
// Not part of this ladder: TEXT_BODY/TEXT_META/TEXT_HEADING (the navy-tinted
// ink of the page chrome) and the semaphore's neutral pair, which share two
// of these hex values but answer to the payload's verdicts, not to rank.
export const INK = {
  strong: "#37474d",
  body: "#4a5a60",
  soft: "#5a6c72",
  muted: "#7c8b91",
  quiet: "#8a97a0",
  label: "#9aa7ac",
  faint: "#a2afb4",
  ghost: "#b7c2bd",
} as const;
